import { useCallback, useEffect, useState } from "react";
import { createOrganization, deleteOrganization, getOrganizations, updateOrganization } from "../api/organizations.api";
import type { Organization, OrganizationPayload } from "../types/organization.types";

const pageSize = 20;
const cityOrganizationType = "city";

export function useOrganizations() {
  const [items, setItems] = useState<Organization[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [keyword, setKeyword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getOrganizations({
        index: page - 1,
        size: pageSize,
        keyword: keyword.trim() || undefined,
        organization_type: cityOrganizationType,
      });
      setItems(response.list);
      setTotal(response.paging?.total ?? response.list.length);
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : "Failed to load organizations");
    } finally {
      setLoading(false);
    }
  }, [keyword, page]);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  const save = useCallback(async (id: string | undefined, payload: OrganizationPayload) => {
    if (id) await updateOrganization(id, payload);
    else await createOrganization(payload);
    await refetch();
  }, [refetch]);

  const remove = useCallback(async (id: string) => {
    await deleteOrganization(id);
    await refetch();
  }, [refetch]);

  return {
    items, total, page, keyword, loading, error, refetch, save, remove,
    setPage, setKeyword,
  };
}
